import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    // Load display theme from public settings
    axios.get(`${API_URL}/api/system-settings/public`).then(res => {
      const theme = res.data?.display_theme;
      if (theme === 'navy') {
        document.documentElement.setAttribute('data-theme', 'navy');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    }).catch(() => {});
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    // If 2FA is required, return early — caller will route to /verify-2fa or /enrol-2fa
    if (response.data?.twofa_required) {
      return response.data;
    }
    // Re-fetch from /auth/me to get full user data including permissions
    const meRes = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
    setUser(meRes.data);
    return response.data;
  };

  const verifyTwoFactor = async (challenge_token, code) => {
    const response = await axios.post(`${API_URL}/api/auth/2fa/verify`,
      { challenge_token, code },
      { withCredentials: true }
    );
    const meRes = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
    setUser(meRes.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    verifyTwoFactor,
    logout,
    checkAuth,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
